
import { useState, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Investor, Contact } from "@/types/investor";
import { useGoogleAuth } from "@/contexts/GoogleAuthContext";
import { loadFromSupabaseThenLocal, saveToSupabaseThenLocal } from "@/utils/investors/databaseUtils";

/**
 * Hook for syncing data with Supabase using optimized batch operations
 */
export const useSupabaseSync = () => {
  const { supabaseSession } = useGoogleAuth();
  const [dataLoaded, setDataLoaded] = useState(false);
  
  // Track logout state to prevent operations after logout
  const isLoggedOutRef = useRef(false);
  
  // Update logout tracking when session changes
  useCallback(() => {
    isLoggedOutRef.current = !supabaseSession;
  }, [supabaseSession]);
  
  /**
   * Load data from Supabase - uses optimized individual record structure
   */
  const loadFromSupabase = useCallback(async () => {
    try {
      // Check if logged out - prevent any loading
      if (isLoggedOutRef.current || !supabaseSession?.user?.id) {
        console.log("Cannot load from Supabase - not authenticated or logged out");
        return null;
      }
      
      console.log("Loading data from Supabase for user:", supabaseSession.user.id);
      
      // Use the optimized loadFromSupabaseThenLocal function 
      const result = await loadFromSupabaseThenLocal(supabaseSession.user.id);
      
      // Get context data
      const { data: contextData, error: contextError } = await supabase
        .from('ai_context')
        .select('*')
        .eq('user_id', supabaseSession.user.id)
        .maybeSingle();
      
      if (contextError) {
        console.error("Error loading context data from Supabase:", contextError);
      }
      
      // Combine results
      setDataLoaded(true);
      
      return {
        investors: result.investors || [],
        contacts: result.contacts || [],
        processedIds: result.processedIds || new Set<string>(),
        businessInfo: contextData?.business_info || "",
        relevanceContext: contextData?.relevance_context || "",
        source: result.source || "database"
      };
    } catch (error) {
      console.error("Error in loadFromSupabase:", error);
      return null;
    }
  }, [supabaseSession]);
  
  /**
   * Sync data to Supabase using batch operations
   */
  const syncToSupabase = useCallback(async (
    investors: Investor[],
    contacts: Contact[],
    processedIds: Set<string>
  ): Promise<boolean> => {
    try {
      // Check if logged out - prevent any syncing
      if (isLoggedOutRef.current || !supabaseSession?.user?.id) {
        console.log("Cannot sync to Supabase - not authenticated or logged out");
        return false;
      }
      
      console.log(`Syncing ${investors.length} investors and ${contacts.length} contacts to Supabase using batch operations`);
      
      // Use the optimized saveToSupabaseThenLocal function with batch processing
      return await saveToSupabaseThenLocal(investors, contacts, processedIds);
    } catch (error) {
      console.error("Error in syncToSupabase:", error);
      return false;
    }
  }, [supabaseSession]);
  
  /**
   * Sync context data to Supabase
   */
  const syncContextToSupabase = useCallback(async (
    relevanceContext: string,
    businessInfo: string
  ): Promise<boolean> => {
    try {
      // Check if logged out - prevent any syncing
      if (isLoggedOutRef.current || !supabaseSession?.user?.id) {
        console.log("Cannot sync context to Supabase - not authenticated or logged out");
        return false;
      }
      
      // Check if we already have a record
      const { data: existingData, error: checkError } = await supabase
        .from('ai_context')
        .select('id')
        .eq('user_id', supabaseSession.user.id)
        .maybeSingle();
      
      if (checkError) {
        console.error("Error checking for existing context:", checkError);
        return false;
      }
      
      if (existingData?.id) {
        // Update existing record
        const { error } = await supabase
          .from('ai_context')
          .update({
            relevance_context: relevanceContext,
            business_info: businessInfo,
            updated_at: new Date().toISOString()
          })
          .eq('id', existingData.id);
        
        if (error) {
          console.error("Error updating context data:", error);
          return false;
        }
      } else {
        // Create new record
        const { error } = await supabase
          .from('ai_context')
          .insert({
            user_id: supabaseSession.user.id,
            relevance_context: relevanceContext,
            business_info: businessInfo
          });
        
        if (error) {
          console.error("Error inserting context data:", error);
          return false;
        }
      }
      
      return true;
    } catch (error) {
      console.error("Error in syncContextToSupabase:", error);
      return false;
    }
  }, [supabaseSession]);
  
  return {
    dataLoaded,
    loadFromSupabase,
    syncToSupabase,
    syncContextToSupabase
  };
};
